import React, { useMemo, useState } from 'react';
import { Button, Flex, Form, Input, InputNumber, Select, Table, Typography } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import CallOutboundPricingHistoryModal from './CallOutboundPricingHistoryModal';
import { READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import { normalizeString } from '@leedpulse-monorepo/utils';
import { AdminFieldInputProps } from '../../AdminFieldInput';
import { continentAsString, EContinent, EPricingStatus, pricingStatusAsString } from '@leedpulse-monorepo/enums';

interface ContinentItem {
  key: EContinent;
  label: string;
  countries: CountryItem[];
}

interface CountryItem {
  key: string; // ISO2 code, what the backend resolves from the dialed number
  label: string;
  dial_codes: string[]; // display and search only, shared codes cannot identify a country
  status: EPricingStatus;
  prices: {
    landline: number | null;
    cellular: number | null;
  };
}

interface CallOutboundPricingTableProps extends AdminFieldInputProps {
  value: {
    label: string;
    continents: ContinentItem[];
  };
}

interface Row {
  key: string;
  iso: string | null; // null on a continent row
  label: string;
  dialCodes: string[];
  countryCount: number | null;
  status: EPricingStatus | null;
  // Name path of the country in the form, anchored on the original indexes so filtering never moves it
  path: (string | number)[] | null;
  children?: Row[];
}

const digitsOf = (value: string): string => value.replace(/\D/g, '');

// Regional indicator symbols: 'FR' becomes the French flag emoji
const flagOf = (iso: string): string => String.fromCodePoint(...[...iso].map((c) => 0x1f1a5 + c.charCodeAt(0)));

const STATUS_OPTIONS = Object.values(EPricingStatus).map((status) => ({
  label: pricingStatusAsString(status),
  value: status,
}));

const CONTINENT_OPTIONS = Object.values(EContinent).map((continent) => ({
  label: continentAsString(continent),
  value: continent,
}));

const CallOutboundPricingTable: React.FC<CallOutboundPricingTableProps> = ({
  namePath,
  label,
  extra,
  value,
  isReadOnly = false,
}) => {
  const [search, setSearch] = useState('');
  const [continentFilter, setContinentFilter] = useState<EContinent | null>(null);
  const [statusFilter, setStatusFilter] = useState<EPricingStatus | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [bulkStatus, setBulkStatus] = useState<EPricingStatus | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // `value` is the schema captured at mount: filters must read the live form values.
  // `preserve`: without it the watch only holds the rows rendered so far, a sparse array
  const form = Form.useFormInstance();
  const liveContinents = Form.useWatch([...namePath, 'continents'], { form, preserve: true }) as ContinentItem[] | undefined;
  const continents = liveContinents ?? value?.continents ?? [];

  const rows = useMemo<Row[]>(() => {
    const sQuery = normalizeString(search.trim());
    const sDigits = digitsOf(search);

    return continents.reduce<Row[]>((acc, continent, iContinent) => {
      if (continentFilter && continent.key !== continentFilter) return acc;

      const aCountries = continent.countries.reduce<Row[]>((aKept, country, iCountry) => {
        if (statusFilter && country.status !== statusFilter) return aKept;

        const bMatchesLabel = !sQuery || normalizeString(country.label).includes(sQuery);
        const bMatchesDialCode = !!sDigits && country.dial_codes.some((code) => digitsOf(code).includes(sDigits));
        if (sQuery && !bMatchesLabel && !bMatchesDialCode) return aKept;

        aKept.push({
          key: `${continent.key}:${country.key}`,
          iso: country.key,
          label: country.label,
          dialCodes: country.dial_codes,
          countryCount: null,
          status: country.status,
          path: [...namePath, 'continents', iContinent, 'countries', iCountry],
        });
        return aKept;
      }, []);

      // A continent whose countries are all filtered out has nothing left to show
      if (aCountries.length === 0) return acc;

      acc.push({
        key: continent.key,
        iso: null,
        label: continent.label || continentAsString(continent.key),
        dialCodes: [],
        countryCount: aCountries.length,
        status: null,
        path: null,
        children: aCountries,
      });
      return acc;
    }, []);
  }, [continents, namePath, search, continentFilter, statusFilter]);

  const iTotal = rows.reduce((acc, row) => acc + (row.countryCount ?? 0), 0);
  const bFiltered = !!search || !!continentFilter || !!statusFilter;

  // Country rows ticked, through their own box or their continent's
  const selectedCountries = useMemo(() => rows.flatMap((row) => row.children ?? []).filter((row) => selectedKeys.includes(row.key)), [rows, selectedKeys]);

  // Prices are left as they are: edited one by one, and frozen outside the extra status
  const applyBulkStatus = () => {
    if (!bulkStatus) return;
    for (const row of selectedCountries) if (row.path) form.setFieldValue([...row.path, 'status'], bulkStatus);
    setSelectedKeys([]);
    setBulkStatus(null);
  };

  const priceCell = (row: Row, sKey: 'landline' | 'cellular') => {
    if (!row.path) return null;
    // Prices only count in extra: kept but frozen while the country is included or blocked
    const bFrozen = isReadOnly || row.status !== EPricingStatus.EXTRA;

    return (
      <Form.Item name={[...row.path, 'prices', sKey]} noStyle>
        <InputNumber
          disabled={bFrozen}
          style={isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }}
          min={0.001}
          max={999}
          step={0.001}
          precision={3}
          decimalSeparator=","
          placeholder="-"
        />
      </Form.Item>
    );
  };

  return (
    <Flex vertical gap={8} style={{ gridColumn: '1/4', border: '1px solid #e0e0e0', padding: 16, borderRadius: 8 }}>
      <Flex align="center" gap={8}>
        <Typography.Title level={5} style={{ margin: 0 }}>{label}</Typography.Title>
        {/* The history lives next to the config, under the admin doc being edited */}
        {form?.getFieldValue('id') && (
          <Button type="text" size="small" icon={<HistoryOutlined />} title="Historique des versions" onClick={() => setHistoryOpen(true)} />
        )}
      </Flex>
      {extra && <Typography.Text type="secondary">{extra}</Typography.Text>}
      {historyOpen && <CallOutboundPricingHistoryModal adminId={form.getFieldValue('id') as string} open={historyOpen} onClose={() => setHistoryOpen(false)} />}

      <Flex justify="space-between" gap={8} wrap="wrap">
        <Flex gap={8} wrap="wrap">
          <Input
            placeholder="Pays ou indicatif"
            allowClear
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelectedKeys([]); }}
            style={{ width: 220 }}
          />
          <Select
            placeholder="Continent"
            allowClear
            options={CONTINENT_OPTIONS}
            value={continentFilter}
            onChange={(selected) => { setContinentFilter(selected ?? null); setSelectedKeys([]); }}
            style={{ width: 180 }}
          />
          <Select
            placeholder="Statut"
            allowClear
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(selected) => { setStatusFilter(selected ?? null); setSelectedKeys([]); }}
            style={{ width: 160 }}
          />
          {selectedCountries.length > 0 && (
            <>
              <Select
                placeholder="Statut à appliquer"
                allowClear
                options={STATUS_OPTIONS}
                value={bulkStatus}
                onChange={(selected) => setBulkStatus(selected ?? null)}
                style={{ width: 180 }}
              />
              <Button type="primary" disabled={!bulkStatus} onClick={applyBulkStatus}>
                Appliquer à {selectedCountries.length} pays
              </Button>
            </>
          )}
        </Flex>

        <Flex gap={8}>
          {!isReadOnly && (
            // Back to the grid as the page loaded it: the form's initial values
            <Button onClick={() => { form.resetFields([namePath]); setSelectedKeys([]); }}>
              Annuler les modifications
            </Button>
          )}
          <Button
            type="text"
            onClick={() => setExpandedKeys(expandedKeys.length > 0 ? [] : rows.map((row) => row.key))}
          >
            {expandedKeys.length > 0 ? 'Tout replier' : 'Tout déplier'}
          </Button>
        </Flex>
      </Flex>

      <Typography.Text type="secondary">
        {iTotal} pays et territoires{bFiltered ? ' (filtrés)' : ''}
      </Typography.Text>

      <Table<Row>
        dataSource={rows}
        rowKey="key"
        size="small"
        pagination={false}
        scroll={{ y: 520 }}
        // A continent box ticks the countries it shows, so a filter narrows what a bulk action touches
        rowSelection={isReadOnly ? undefined : { selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys), checkStrictly: false }}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
        }}
        columns={[
          {
            title: 'Pays',
            dataIndex: 'label',
            key: 'label',
            render: (sLabel: string, row: Row) =>
              row.iso ? `${flagOf(row.iso)} ${sLabel}` : <Typography.Text strong>{sLabel} ({row.countryCount})</Typography.Text>,
          },
          {
            title: 'Indicatif',
            dataIndex: 'dialCodes',
            key: 'dialCodes',
            width: 140,
            // A continent row has no dial code of its own: left empty
            render: (aDialCodes: string[], row: Row) => {
              if (!row.iso) return null;
              return aDialCodes.length ? `+${aDialCodes.join(', +')}` : 'N/A';
            },
          },
          {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            width: 170,
            render: (_: EPricingStatus, row: Row) =>
              row.path && (
                <Form.Item name={[...row.path, 'status']} noStyle>
                  <Select
                    disabled={isReadOnly}
                    options={STATUS_OPTIONS}
                    style={isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }}
                  />
                </Form.Item>
              ),
          },
          {
            // Prices are what the backend bills: euros per minute, tax excluded
            title: 'Tarif hors forfait (€/min HT)',
            key: 'prices',
            children: [
              {
                title: 'Fixe',
                key: 'prices.landline',
                width: 160,
                render: (_: unknown, row: Row) => priceCell(row, 'landline'),
              },
              {
                title: 'Mobile',
                key: 'prices.cellular',
                width: 160,
                render: (_: unknown, row: Row) => priceCell(row, 'cellular'),
              },
            ],
          },
        ]}
      />
    </Flex>
  );
};

export default CallOutboundPricingTable;
